import type { LoaderFunctionArgs } from '@remix-run/node'
import { useLoaderData, useLocation } from '@remix-run/react'
import { useEffect } from 'react'
import {
  aDrawnFeature,
  aMapDrawIsOpen,
  aMapFilters,
  aMapRef,
  aPersistedFilters,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import {
  DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
  clearRememberedDrawnFeatureForBackNavigation,
  normalizeRestoredDrawFeature,
} from '~/components'
import { useEmbed, useMapCardSizes } from '~/hooks'
import { locationToFeature } from '~/models'
import { findSearch } from '~/mongo.server'
import { getOrganizationSlug } from '~/utils.server'
import Container from './Container'
export { ErrorBoundary } from '~/components'

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const organizationSlug = getOrganizationSlug(request)
  const url = new URL(request.url)
  const searchParams = url.searchParams
  const searchSlug = searchParams.get('savedSearch')
  if (searchSlug) {
    const search = await findSearch({
      'organization.slug': organizationSlug,
      slug: searchSlug,
    })
    if (search) {
      const modified = searchParams.get('modified')
      return { modified, search }
    }
  }
  return null
}

export default function MapLayout() {
  const data = useLoaderData<typeof loader>()
  const setDrawnFeature = useSetAtom(aDrawnFeature)
  const mapRef = useAtomValue(aMapRef)
  const setIsDrawOpen = useSetAtom(aMapDrawIsOpen)
  const setMapFilters = useSetAtom(aMapFilters)
  const setPersistedFilters = useSetAtom(aPersistedFilters)
  const mapCardSizes = useMapCardSizes()

  const { isEmbed, updateHostPageURL } = useEmbed()
  const location = useLocation()

  useEffect(() => {
    if (isEmbed && location.pathname === '/') {
      updateHostPageURL()
    }
  }, [isEmbed, location.pathname, updateHostPageURL])

  useEffect(() => {
    if (data && mapRef) {
      if (data.modified) {
        data.search.filters.modified = data.modified.split('_')
      }

      setTimeout(() => {
        mapRef.flyTo({
          center: [data.search.lng, data.search.lat],
          zoom: data.search.z,
          padding: { left: mapCardSizes.list, top: 0, right: 0, bottom: 0 },
          duration: DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
        })
        setPersistedFilters({})
        setMapFilters(data.search.filters)

        const geometry = data.search.location.$geoWithin.$geometry
        const isDrawnFeature = 'properties' in geometry

        if (isDrawnFeature) {
          setIsDrawOpen(true)
          const feature = normalizeRestoredDrawFeature(
            locationToFeature(data.search.location.$geoWithin.$geometry)
          )
          setDrawnFeature(feature)
        } else {
          clearRememberedDrawnFeatureForBackNavigation()
          setDrawnFeature(null)
          setIsDrawOpen(false)
        }
      }, 200)
    }
  }, [
    data,
    mapCardSizes.list,
    mapRef,
    setMapFilters,
    setDrawnFeature,
    setIsDrawOpen,
  ])

  return <Container />
}
